import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Avatar } from '@/components/Avatar';
import { Topbar } from '@/components/Topbar';
import { Icon } from '@/components/Icon';
import { fmtRelative } from '@/lib/format';
import { haptic } from '@/lib/telegram';
import { openTelegramUrl } from '@/lib/telegram-links';
import type { ChatMessage } from '@/types/domain';

export function CampaignChatPage(): JSX.Element {
  const { threadId } = useParams();
  const tid = decodeURIComponent(threadId ?? '');
  const { data: thread, loading, reload } = useAsync(() => api.getInboxThread(tid), [tid]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Скролл вниз при появлении новых сообщений
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    haptic('success');
    setDraft('');
    await api.sendInboxMessage(tid, text);
    setBusy(false);
    reload();
  };

  if (loading || !thread) {
    return (
      <div className="space-y-4">
        <Topbar title="Чат" />
        <div className="skeleton h-20" />
        <div className="skeleton h-32" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-32px)] flex-col">
      <Topbar
        title={thread.fromName}
        subtitle={`${thread.fromUsername ?? ''}${thread.fromUsername ? ' · ' : ''}от ${thread.accountUsername}`}
        right={
          thread.kind === 'dm' ? (
            <button
              type="button"
              onClick={() => openTelegramUrl(thread.peerUrl)}
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-ink-700/60 bg-ink-850 text-ink-300 active:scale-95"
              aria-label="Открыть аккаунт"
            >
              <Icon name="message" size={18} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => openTelegramUrl(thread.chatUrl || thread.messages.at(-1)?.messageUrl)}
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-ink-700/60 bg-ink-850 text-ink-300 active:scale-95"
              aria-label="Открыть чат"
            >
              <Icon name="message" size={18} />
            </button>
          )
        }
      />

      {/* Лента сообщений */}
      <div ref={scrollRef} className="-mx-4 flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-3">
          {thread.messages.map((m) => <MessageBubble key={m.id} msg={m} fromName={thread.fromName} />)}
        </div>
      </div>

      {/* Composer */}
      <div className="-mx-4 sticky bottom-0 z-30 border-t border-white/5 bg-black/70 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">
        <div className="flex items-end gap-2">
          <textarea
            className="input min-h-[48px] resize-none py-3 text-sm leading-snug"
            placeholder="Написать вручную…"
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <button
            onClick={send}
            disabled={!draft.trim() || busy}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white shadow-soft active:scale-95 disabled:opacity-40"
            aria-label="отправить"
          >
            <Icon name="upload" size={18} />
          </button>
        </div>
        <div className="mt-1.5 flex items-center justify-between text-2xs text-ink-500">
          <span>отправляем от {thread.accountUsername}</span>
          <span>Enter — отправить · Shift+Enter — перенос</span>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, fromName }: { msg: ChatMessage; fromName: string }): JSX.Element {
  // Слева — собеседник, справа — мы (ai/me)
  const fromUs = msg.side !== 'them';
  const isAi = msg.side === 'ai';
  const clickable = Boolean(msg.messageUrl);

  return (
    <div className={`flex items-end gap-2 ${fromUs ? 'flex-row-reverse' : ''}`}>
      <div className="shrink-0">
        {fromUs
          ? <span className={`flex h-7 w-7 items-center justify-center rounded-full text-2xs font-bold ${isAi ? 'bg-accent-500/30 text-accent-200' : 'bg-white text-ink-950'}`}>{isAi ? 'AI' : 'Я'}</span>
          : <Avatar name={fromName} size={28} />
        }
      </div>
      <div className={`max-w-[78%] space-y-0.5 ${fromUs ? 'items-end' : 'items-start'} flex flex-col`}>
        {isAi && (
          <span className="text-2xs uppercase tracking-wider text-accent-300">авто-ответ нейронки</span>
        )}
        <button
          type="button"
          disabled={!clickable}
          onClick={() => openTelegramUrl(msg.messageUrl)}
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
            !fromUs
              ? 'rounded-bl-md bg-ink-800/80 text-ink-100'
              : isAi
                ? 'rounded-br-md border border-accent-500/30 bg-accent-500/15 text-ink-50'
                : 'rounded-br-md bg-accent-500 text-white'
          } ${clickable ? 'cursor-pointer transition active:scale-[0.99]' : 'cursor-default'}`}
        >
          {msg.text}
        </button>
        <span className="px-1 text-2xs text-ink-500">
          {fmtRelative(msg.at)}{msg.status && msg.status !== 'posted' ? ` · ${msg.status}` : ''}
        </span>
      </div>
    </div>
  );
}
